import { createId } from '@/core/id';
import { HttpError, toChatError } from '@/transport/errors';
const DEFAULT_PATHS = {
    createConversation: '/conversations',
    sendMessage: '/conversations/:id/messages',
    history: '/conversations/:id/messages',
    feedback: '/conversations/:id/messages/:messageId/feedback',
    handoff: '/conversations/:id/handoff',
};
/**
 * HTTP adapter. Handles both plain JSON request/response and SSE streaming,
 * selected by `options.stream`. Swap `MockTransport` for this in the config
 * and nothing else in the app changes.
 */
export class RestTransport {
    options;
    name = 'rest';
    capabilities;
    paths;
    doFetch;
    constructor(options) {
        this.options = options;
        this.paths = { ...DEFAULT_PATHS, ...options.paths };
        this.doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
        this.capabilities = {
            streaming: options.stream ?? false,
            push: false,
            history: true,
            feedback: true,
            handoff: true,
        };
    }
    async createConversation(input) {
        const raw = await this.request(this.paths.createConversation, {
            method: 'POST',
            body: JSON.stringify({ context: input.context, locale: input.locale }),
        });
        return {
            id: String(raw.id ?? raw.conversationId ?? createId('conv')),
            createdAt: Number(raw.createdAt ?? Date.now()),
            status: raw.status ?? 'open',
            metadata: raw.metadata,
        };
    }
    async loadHistory(conversationId) {
        const raw = await this.request(this.path(this.paths.history, { id: conversationId }), { method: 'GET' });
        const list = Array.isArray(raw) ? raw : (raw.messages ?? []);
        return list.map((item) => this.normalise(item, conversationId));
    }
    async sendMessage({ conversationId, message, replyId, context }, emit, signal) {
        const url = this.path(this.paths.sendMessage, { id: conversationId });
        const body = JSON.stringify({
            content: message.content,
            messageId: message.id,
            replyId,
            context,
            stream: this.capabilities.streaming,
        });
        try {
            emit({ type: 'typing', isTyping: true });
            if (!this.capabilities.streaming) {
                const raw = await this.request(url, { method: 'POST', body }, signal);
                const parsed = this.normalise(raw, conversationId);
                emit({ type: 'typing', isTyping: false });
                emit({
                    type: 'message.start',
                    message: { ...parsed, id: replyId, content: '', status: 'pending' },
                });
                emit({ type: 'message.delta', messageId: replyId, delta: parsed.content });
                if (parsed.sources?.length) {
                    emit({ type: 'message.sources', messageId: replyId, sources: parsed.sources });
                }
                if (parsed.actions?.length) {
                    emit({ type: 'message.actions', messageId: replyId, actions: parsed.actions });
                }
                if (parsed.suggestions?.length) {
                    emit({ type: 'message.suggestions', messageId: replyId, suggestions: parsed.suggestions });
                }
                emit({ type: 'message.complete', messageId: replyId });
                return;
            }
            await this.streamSse(url, body, replyId, conversationId, emit, signal);
        }
        catch (err) {
            emit({ type: 'typing', isTyping: false });
            const error = toChatError(err);
            if (error.code === 'aborted')
                return;
            emit({ type: 'message.error', messageId: replyId, error });
        }
    }
    async sendFeedback({ conversationId, messageId, value, comment }) {
        await this.request(this.path(this.paths.feedback, { id: conversationId, messageId }), {
            method: 'POST',
            body: JSON.stringify({ value, comment }),
        });
    }
    async requestHandoff(conversationId, reason) {
        await this.request(this.path(this.paths.handoff, { id: conversationId }), {
            method: 'POST',
            body: JSON.stringify({ reason }),
        });
    }
    /**
     * Reads an SSE body. Each frame is expected to be
     * `data: {"type":"delta","text":"..."}` — adjust `handleFrame` to match
     * your server's event names.
     */
    async streamSse(url, body, replyId, conversationId, emit, signal) {
        const response = await this.doFetch(this.url(url), {
            method: 'POST',
            body,
            signal,
            headers: { ...(await this.buildHeaders()), Accept: 'text/event-stream' },
        });
        if (!response.ok || !response.body) {
            throw new HttpError(response.status, `Stream failed: ${response.status}`);
        }
        emit({ type: 'typing', isTyping: false });
        emit({
            type: 'message.start',
            message: {
                id: replyId,
                conversationId,
                role: 'assistant',
                content: '',
                status: 'streaming',
                createdAt: Date.now(),
                updatedAt: Date.now(),
            },
        });
        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
        let buffer = '';
        while (true) {
            const { done, value } = await reader.read();
            if (done)
                break;
            buffer += value;
            const frames = buffer.split('\n\n');
            buffer = frames.pop() ?? '';
            for (const frame of frames)
                this.handleFrame(frame, replyId, emit);
        }
        emit({ type: 'message.complete', messageId: replyId });
    }
    handleFrame(frame, replyId, emit) {
        const line = frame
            .split('\n')
            .find((l) => l.startsWith('data:'))
            ?.slice(5)
            .trim();
        if (!line || line === '[DONE]')
            return;
        try {
            const payload = JSON.parse(line);
            switch (payload.type) {
                case 'delta':
                case 'token':
                    emit({ type: 'message.delta', messageId: replyId, delta: String(payload.text ?? '') });
                    break;
                case 'sources':
                    emit({
                        type: 'message.sources',
                        messageId: replyId,
                        sources: payload.sources,
                    });
                    break;
                case 'suggestions':
                    emit({
                        type: 'message.suggestions',
                        messageId: replyId,
                        suggestions: payload.suggestions,
                    });
                    break;
                case 'actions':
                    emit({ type: 'message.actions', messageId: replyId, actions: payload.actions });
                    break;
                default:
                    break;
            }
        }
        catch {
            // Ignore malformed frames rather than killing the stream.
        }
    }
    normalise(raw, conversationId) {
        const custom = this.options.parseMessage?.(raw) ?? {};
        const obj = (raw ?? {});
        const now = Date.now();
        return {
            id: String(obj.id ?? createId('msg')),
            conversationId,
            role: obj.role ?? 'assistant',
            content: String(obj.content ?? obj.text ?? ''),
            status: 'complete',
            createdAt: Number(obj.createdAt ?? now),
            updatedAt: Number(obj.updatedAt ?? now),
            author: obj.author,
            sources: obj.sources,
            suggestions: obj.suggestions,
            actions: obj.actions,
            ...custom,
        };
    }
    async request(path, init, signal) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(new DOMException('Request timed out.', 'TimeoutError')), this.options.timeoutMs ?? 5 * 60_000);
        signal?.addEventListener('abort', () => controller.abort(), { once: true });
        try {
            const response = await this.doFetch(this.url(path), {
                ...init,
                signal: controller.signal,
                headers: { ...(await this.buildHeaders()), ...init.headers },
            });
            if (!response.ok) {
                throw new HttpError(response.status, `Request failed: ${response.status}`, await safeJson(response));
            }
            return (await safeJson(response));
        }
        finally {
            clearTimeout(timeout);
        }
    }
    async buildHeaders() {
        const key = typeof this.options.apiKey === 'function' ? await this.options.apiKey() : this.options.apiKey;
        return {
            'Content-Type': 'application/json',
            ...(key ? { Authorization: `Bearer ${key}` } : {}),
            ...this.options.headers,
        };
    }
    url(path) {
        return `${this.options.baseUrl.replace(/\/$/, '')}${path}`;
    }
    path(template, params) {
        return template.replace(/:(\w+)/g, (_, key) => encodeURIComponent(params[key] ?? ''));
    }
}
async function safeJson(response) {
    const text = await response.text();
    if (!text)
        return {};
    try {
        return JSON.parse(text);
    }
    catch {
        return { content: text };
    }
}
